import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, WarningCircle, Package, Scales, ClipboardText,
  MicrosoftExcelLogo, Receipt, Plus,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { DashboardZone, ActionTile, ListCard, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { shizaiApi, ORDER_STATUS_BADGE, yen } from './shizaiApi';
import ExportMonthlyDialog from './ExportMonthlyDialog';

/**
 * 資材ダッシュボード — decision-driven (Jun 2026).
 * Zones: 対応が必要 → 進行中の発注 → 最近の入荷 → (任意)参考指標.
 * Removed by the Decision Value Test: 「今月のサマリー」KPI strip
 * (進行中 / 今月入荷 / 下書き / 未払い — 未払い duplicated the invoice tile)
 * and the 部門別未処理 badge row (counts with no action attached). The two
 * remaining reference numbers live behind 参考指標.
 */
export default function ShizaiDashboard() {
  const { t } = useT();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ open_requests: [], in_progress: [], recent_received: [], by_department: [] });
  const [departments, setDepartments] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(() => {
    shizaiApi.stats().then(setStats).catch((err) => console.error('[shizai] stats failed', err));
    shizaiApi.dashboard().then(setData).catch((err) => console.error('[shizai] dashboard failed', err));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { shizaiApi.departments().then(setDepartments).catch(() => {}); }, []);

  const todos = [
    { key: 'approve', Icon: ShieldCheck, value: stats?.approvals_pending ?? 0, urgent: false, onClick: () => navigate('/shizai/approvals'), testid: 'shizai-todo-approve' },
    { key: 'overdue', Icon: WarningCircle, value: stats?.overdue ?? 0, urgent: true, onClick: () => navigate('/shizai/orders', { state: { filter: 'overdue' } }), testid: 'shizai-todo-overdue' },
    { key: 'partial', Icon: Package, value: stats?.partial_received ?? 0, urgent: false, onClick: () => navigate('/shizai/receiving'), testid: 'shizai-todo-partial' },
    { key: 'invoice', Icon: Scales, value: stats?.unpaid ?? 0, urgent: false, onClick: () => navigate('/shizai/invoices'), testid: 'shizai-todo-invoice' },
    { key: 'request', Icon: ClipboardText, value: stats?.open_requests ?? 0, urgent: false, onClick: () => navigate('/shizai/kanban'), testid: 'shizai-todo-request' },
  ];
  const nothingToDo = stats && todos.every((td) => (Number(td.value) || 0) === 0);

  return (
    <div className="space-y-8 page-enter" data-testid="shizai-dashboard">
      <PageHeader
        testid="shizai-dashboard-header"
        title={t('shizai.title')}
        description={t('shizai.subtitle')}
        menuItems={[
          { label: '経理データ出力', icon: <MicrosoftExcelLogo size={16} weight="duotone" />, onClick: () => setExportOpen(true), testid: 'export-monthly-btn' },
          { label: '請求書照合', icon: <Scales size={16} weight="duotone" />, onClick: () => navigate('/shizai/invoices'), testid: 'invoices-nav-btn' },
        ]}
        primary={(
          <Button onClick={() => navigate('/shizai/requests/new')} data-testid="dashboard-new-request-btn" className="h-10">
            <Plus size={17} weight="bold" className="mr-1.5" /> 新規依頼作成
          </Button>
        )}
      />

      {/* Zone 1 — 対応が必要 */}
      <DashboardZone title="対応が必要" hint="クリックすると該当画面に移動します" testid="shizai-todo-section">
        {nothingToDo ? (
          <AllClear message="いま対応が必要な依頼・発注はありません" testid="shizai-todo-allclear" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {todos.map((td) => (
              <ActionTile
                key={td.key}
                icon={td.Icon}
                label={t(`shizai.dash.todo.${td.key}`)}
                hint={t(`shizai.dash.todo.${td.key}.hint`)}
                value={td.value}
                urgent={td.urgent}
                onClick={td.onClick}
                testid={td.testid}
              />
            ))}
          </div>
        )}
      </DashboardZone>

      {/* Zone 2/3 — 例外・進行中 / 最近の活動 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ListCard
          title={t('shizai.dash.inProgress')}
          icon={Receipt}
          count={data.in_progress.length}
          action={<Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate('/shizai/orders')} data-testid="shizai-dash-inprogress-all">{t('shizai.all')}</Button>}
          testid="shizai-dash-inprogress"
        >
          {data.in_progress.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-zinc-400">{t('shizai.dash.noOrders')}</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {data.in_progress.slice(0, 8).map((o) => (
                <li key={o.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50/50 cursor-pointer"
                  onClick={() => navigate('/shizai/orders', { state: { openOrderId: o.id } })} data-testid={`shizai-dash-prog-${o.id}`}>
                  <span className="font-mono text-xs text-zinc-500 shrink-0">{o.order_no}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-800 truncate">{o.supplier_name || '—'}</p>
                    <p className="text-xs text-zinc-400 truncate">{(o.lines?.[0]?.name) || ''}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant="outline" className={ORDER_STATUS_BADGE[o.status]}>{t(`shizai.orderStatus.${o.status}`)}</Badge>
                    {o.due_date && <p className={`text-[11px] mt-0.5 ${o.overdue ? 'text-rose-600 font-semibold' : 'text-zinc-400'}`}>納期 {o.due_date}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ListCard>

        <ListCard
          title={t('shizai.dash.recent')}
          icon={Package}
          count={data.recent_received.length}
          action={<Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate('/shizai/history')} data-testid="shizai-dash-recent-all">{t('shizai.all')}</Button>}
          testid="shizai-dash-recent"
        >
          {data.recent_received.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-zinc-400">{t('shizai.dash.noRecent')}</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {data.recent_received.slice(0, 8).map((o) => (
                <li key={o.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50/50 cursor-pointer"
                  onClick={() => navigate('/shizai/orders', { state: { openOrderId: o.id } })}>
                  <span className="font-mono text-xs text-zinc-500 shrink-0">{o.order_no}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-800 truncate">{o.supplier_name || '—'}</p>
                    <p className="text-xs text-zinc-400 truncate">{(o.lines?.[0]?.name) || ''}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-zinc-800 tabular-nums">{yen(o.total)}</p>
                    {o.received_at && <p className="text-[11px] text-zinc-400">{o.received_at}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      </div>

      {/* Zone 6 — optional reference numbers */}
      <MetricsDisclosure title="参考指標（今月）" testid="shizai-metrics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          {[
            { label: t('shizai.stat.inProgress'), value: stats?.in_progress ?? 0 },
            { label: t('shizai.stat.received'), value: stats?.received_this_month ?? 0 },
            { label: t('shizai.stat.draft'), value: stats?.draft ?? 0 },
            { label: '部門別 未処理', value: (data.by_department || []).reduce((s, b) => s + (b.count || 0), 0) },
          ].map((k) => (
            <div key={k.label} className="rounded-lg border border-zinc-200 p-3">
              <p className="text-[11px] text-zinc-500">{k.label}</p>
              <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{k.value}</p>
            </div>
          ))}
        </div>
        {(data.by_department || []).length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-3" data-testid="shizai-by-dept-section">
            {data.by_department.map((b) => (
              <span key={b.department_id || 'none'} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-zinc-200 text-xs">
                <span className="w-2 h-2 rounded-full" style={{ background: b.color || '#a1a1aa' }} />
                <span className="text-zinc-600">{b.name}</span>
                <span className="font-medium text-zinc-900 tabular-nums">{b.count}</span>
              </span>
            ))}
          </div>
        )}
      </MetricsDisclosure>

      <ExportMonthlyDialog open={exportOpen} onClose={() => setExportOpen(false)} departments={departments} />
    </div>
  );
}
